import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LogOut, User, PanelLeftClose, PanelLeftOpen, Moon, Sun, Search, Replace, ListChecks, BarChart3, Menu, Compass, BookOpen } from 'lucide-react';
import { LogoMark, Wordmark } from '../brand/Logo';
import { useAuth } from '../../contexts/authContext';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { Hint } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { DEV_AUTH_BYPASS } from '../../config';

const NAV = [
  { to: '/explore', label: 'Explore', icon: Search, match: ['/explore', '/item'] },
  { to: '/substitute', label: 'Substitute', icon: Replace },
  { to: '/shortlists', label: 'Shortlists', icon: ListChecks },
  { to: '/overview', label: 'Overview', icon: BarChart3 },
  { to: '/guide', label: 'How it works', icon: BookOpen },
];

const readBool = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
};

function useDarkMode() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);
  return [dark, setDark];
}

function Brand({ collapsed, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex min-w-0 items-center gap-2.5 text-left">
      <LogoMark className="h-8 w-8 text-foreground" />
      {!collapsed && (
        <span className="min-w-0">
          <Wordmark className="h-4 text-foreground" />
          <span className="mt-1 block whitespace-nowrap text-[10.5px] leading-tight text-muted-foreground">Problem stock explorer</span>
        </span>
      )}
    </button>
  );
}

function NavItems({ collapsed, onNavigate }) {
  const { pathname } = useLocation();
  return (
    <nav className="space-y-0.5">
      {NAV.map(({ to, label, icon: Icon, match }) => {
        const active = (match || [to]).some((p) => pathname === p || pathname.startsWith(`${p}/`));
        const link = (
          <NavLink
            key={to}
            to={to}
            data-tour={`nav-${to}`}
            onClick={onNavigate}
            className={cn(
              'flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium transition-colors',
              active ? 'bg-background text-foreground shadow-[0_0_0_1px_hsl(var(--border))]' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              collapsed && 'justify-center px-0'
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {!collapsed && label}
          </NavLink>
        );
        return collapsed ? (
          <Hint key={to} text={label} side="right">
            {link}
          </Hint>
        ) : (
          link
        );
      })}
    </nav>
  );
}

function TourButton({ collapsed }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const start = () => {
    if (pathname.startsWith('/substitute')) navigate('/substitute?tour=1');
    else navigate('/explore?tour=1');
  };
  return (
    <button
      type="button"
      onClick={start}
      data-tour="tour-button"
      className={cn(
        'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground',
        collapsed && 'justify-center px-0'
      )}
      aria-label="Take the guided tour"
      title="Take the guided tour"
    >
      <Compass className="h-4 w-4" />
      {!collapsed && 'Take the tour'}
    </button>
  );
}

function UserMenu({ collapsed, dark, setDark }) {
  const { user, logout } = useAuth();
  const initials = (user?.displayName || 'U').slice(0, 2).toUpperCase();
  return (
    <div className="space-y-1">
      <TourButton collapsed={collapsed} />
      <button
        type="button"
        onClick={() => setDark((d) => !d)}
        className={cn(
          'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground',
          collapsed && 'justify-center px-0'
        )}
        aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        {!collapsed && (dark ? 'Light mode' : 'Dark mode')}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium transition hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              collapsed && 'justify-center px-0'
            )}
            title={user?.displayName}
          >
            <Avatar className="h-7 w-7">
              <AvatarImage src={user?.clerkImageUrl || user?.profile?.avatar} alt={user?.displayName} />
              <AvatarFallback className="text-[11px]">{initials}</AvatarFallback>
            </Avatar>
            {!collapsed && <span className="truncate">{user?.displayName}</span>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="w-56">
          <DropdownMenuLabel className="flex items-center gap-2 font-normal text-muted-foreground">
            <User className="h-4 w-4" />
            <span className="truncate">{user?.displayName}</span>
          </DropdownMenuLabel>
          {DEV_AUTH_BYPASS && <div className="px-2 pb-1.5 text-xs text-amber-600">Dev auth bypass active</div>}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => logout()} className="gap-2">
            <LogOut className="h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function AppLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(() => readBool('sidebarCollapsed', false));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useDarkMode();

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => setMobileOpen(false), [pathname]);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'sticky top-0 hidden h-screen shrink-0 flex-col justify-between border-r bg-sidebar transition-[width] duration-200 lg:flex',
          collapsed ? 'w-[60px]' : 'w-56'
        )}
      >
        <div className="space-y-5 px-2.5 py-4">
          <div className={cn('flex items-center gap-1', collapsed ? 'flex-col' : 'justify-between pl-1')}>
            <Brand collapsed={collapsed} onClick={() => navigate('/explore')} />
            <Hint text={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} side="right">
              <button
                type="button"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                onClick={() => setCollapsed((c) => !c)}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
              </button>
            </Hint>
          </div>
          <NavItems collapsed={collapsed} />
        </div>
        <div className="px-2.5 pb-4">
          <UserMenu collapsed={collapsed} dark={dark} setDark={setDark} />
        </div>
      </aside>

      {/* Mobile top bar + drawer */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Brand collapsed={false} onClick={() => navigate('/explore')} />
        </header>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="justify-between p-3">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <div className="space-y-5 pt-1">
              <Brand collapsed={false} onClick={() => navigate('/explore')} />
              <NavItems collapsed={false} onNavigate={() => setMobileOpen(false)} />
            </div>
            <UserMenu collapsed={false} dark={dark} setDark={setDark} />
          </SheetContent>
        </Sheet>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
